import { useState } from "react";
import { Scan, AlertTriangle, Shield, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DashboardLayout } from "@/pages/DashboardPage";
import { useAuth, API } from "@/App";
import axios from "axios";

export default function BreachCheckerPage() {
  const { token } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleScan = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    setLoading(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const resp = await axios.post(`${API}/tools/breach-check-enhanced`, { email }, { headers, withCredentials: true });
      setResult(resp.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Scan failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const riskColors = {
    critical: { bg: "bg-red-500/10", border: "border-red-500/20", text: "text-red-400", label: "CRITICAL" },
    high: { bg: "bg-red-500/10", border: "border-red-500/20", text: "text-red-400", label: "HIGH" },
    medium: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400", label: "MEDIUM" },
    low: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400", label: "LOW" },
  };

  const risk = result ? riskColors[result.risk_level] || riskColors.low : null;

  return (
    <DashboardLayout title="Data Breach Scanner">
      <div className="max-w-3xl space-y-6" data-testid="breach-checker-page">
        <p data-testid="breach-demo-disclosure" className="text-xs text-amber-200 border-l-2 border-amber-500 pl-3">DEMO / AI-GENERATED ANALYSIS. SpyCloud is mocked and HIBP requires an API key. Unverified breach names, counts and risk scores must not be treated as evidence of an actual breach.</p>
        {/* Input */}
        <div className="trace-border rounded-sm bg-zinc-900 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-sm bg-blue-600/10 flex items-center justify-center">
              <Scan className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Email Breach Scanner</h2>
              <p className="text-xs text-zinc-500">AI-powered analysis of your digital exposure</p>
            </div>
          </div>
          <form onSubmit={handleScan} className="flex gap-3">
            <div className="flex-1">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email to scan..."
                className="bg-zinc-950 border-zinc-800 focus:ring-1 focus:ring-blue-500 rounded-sm h-11 text-zinc-50 placeholder:text-zinc-600"
                required
                data-testid="breach-email-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-500 text-white h-11 px-6 rounded-sm font-semibold"
              data-testid="breach-scan-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Scan"}
            </Button>
          </form>
          {error && <p className="mt-3 text-sm text-red-400" data-testid="breach-error">{error}</p>}
        </div>

        {/* Loading */}
        {loading && (
          <div className="trace-border rounded-sm bg-zinc-900 p-12 flex flex-col items-center justify-center gap-4">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            <p className="text-sm text-zinc-400">Preparing analysis and available source results...</p>
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <div className="space-y-4 fade-up" data-testid="breach-results">
            {/* Risk Level */}
            <div className={`trace-border rounded-sm ${risk.bg} border ${risk.border} p-6 flex items-center justify-between`}>
              <div className="flex items-center gap-4">
                {result.risk_level === "low" ? (
                  <Shield className="w-8 h-8 text-emerald-400" strokeWidth={1.5} />
                ) : (
                  <AlertTriangle className="w-8 h-8 text-red-400" strokeWidth={1.5} />
                )}
                <div>
                  <div className={`text-xs uppercase tracking-[0.2em] font-bold ${risk.text}`}>{risk.label} RISK</div>
                  <div className="text-sm text-zinc-300 mt-1">{result.breaches_found} breach(es) detected</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-black tracking-tighter" style={{ fontFamily: 'var(--font-heading)' }}>
                  <span className={risk.text}>{result.overall_score}</span>
                  <span className="text-zinc-600 text-lg">/100</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Safety Score</div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
              <div className="trace-border rounded-sm bg-zinc-900 p-4 text-center">
                <div className="text-2xl font-black tracking-tighter text-zinc-50" style={{ fontFamily: 'var(--font-heading)' }}>{result.dark_web_mentions}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mt-1">Dark Web Mentions</div>
              </div>
              <div className="trace-border rounded-sm bg-zinc-900 p-4 text-center">
                <div className="text-2xl font-black tracking-tighter text-zinc-50" style={{ fontFamily: 'var(--font-heading)' }}>{result.data_broker_listings}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mt-1">Data Broker Listings</div>
              </div>
              <div className="trace-border rounded-sm bg-zinc-900 p-4 text-center">
                <div className="text-2xl font-black tracking-tighter text-zinc-50" style={{ fontFamily: 'var(--font-heading)' }}>{result.breaches_found}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mt-1">Breaches Found</div>
              </div>
            </div>

            {/* Breach Details */}
            {result.breach_details && result.breach_details.length > 0 && (
              <div className="trace-border rounded-sm bg-zinc-900 p-6">
                <h3 className="text-sm font-semibold tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Breach Details</h3>
                <div className="space-y-3">
                  {result.breach_details.map((b, i) => (
                    <div key={i} className="bg-zinc-800/50 rounded-sm p-3 border border-zinc-800">
                      <div className="flex justify-between items-start">
                        <span className="text-sm font-medium text-zinc-200">{b.source}</span>
                        <span className="text-xs text-zinc-500 font-mono">{b.date}</span>
                      </div>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {b.data_exposed?.map((d) => (
                          <span key={d} className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded-sm">{d}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            <div className="trace-border rounded-sm bg-zinc-900 p-6">
              <h3 className="text-sm font-semibold tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Recommendations</h3>
              <div className="space-y-2">
                {result.recommendations?.map((r, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" strokeWidth={1.5} />
                    {r}
                  </div>
                ))}
              </div>
            </div>

            {/* Data Sources */}
            {result.sources && (
              <div className="trace-border rounded-sm bg-zinc-900 p-6">
                <h3 className="text-sm font-semibold tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Intelligence Sources</h3>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(result.sources).map(([source, status]) => (
                    <div key={source} className="bg-zinc-800/50 rounded-sm p-3 border border-zinc-800 text-center">
                      <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${status === 'active' || status === 'connected' ? 'text-emerald-400' : status === 'demo_mode' ? 'text-amber-400' : 'text-zinc-500'}`}>
                        {status === 'active' || status === 'connected' ? 'Connected' : status === 'demo_mode' ? 'Demo Mode' : 'Pending'}
                      </div>
                      <div className="text-xs text-zinc-400 mt-1 capitalize">{source.replace('_', ' ')}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
